/**
 * FRONT-2436: the browser's copy of the backend's in-memory search matcher
 * (`matchesSearchInMemory` in `src/core/text-match.ts`), for the command
 * palette sections that filter an already-loaded list rather than calling a
 * search endpoint: agents, scheduled jobs, skills, workflows and knowledge
 * bases.
 *
 * Those sections sit next to sections the server searches, and the palette's
 * footer advertises the same four operators for all of them ("exact phrase",
 * OR, AND, -exclude). A plain substring match here would make one typed query
 * mean two things depending on which section answered it.
 *
 * This is a hand-copied twin, not a shared import: the frontend bundle does not
 * import backend source. `search-match.test.ts` runs BOTH copies over the same
 * corpus and fails on any disagreement, so the two cannot drift silently.
 * Change the backend first, then this file, then run that test.
 */

/** One token as it appeared in the raw search string, before sanitisation. */
interface SearchToken {
  text: string;
  quoted: boolean;
  negated: boolean;
}

const MAX_SEARCH_LEXEMES = 100;

function tokenizeSearch(raw: string): SearchToken[] {
  const s = (raw || '').normalize('NFKC');
  const out: SearchToken[] = [];
  let i = 0;
  while (i < s.length) {
    if (/\s/.test(s[i]!)) {
      i++;
      continue;
    }
    let negated = false;
    if (s[i] === '-') {
      const next = s[i + 1];
      if (next !== undefined && !/\s/.test(next)) {
        negated = true;
        i++;
      } else {
        i++;
        continue;
      }
    }
    if (s[i] === '"') {
      i++;
      const start = i;
      while (i < s.length && s[i] !== '"') i++;
      const text = s.slice(start, i);
      if (i < s.length) i++;
      out.push({ text, quoted: true, negated });
      continue;
    }
    const start = i;
    while (i < s.length && !/\s/.test(s[i]!)) i++;
    out.push({ text: s.slice(start, i), quoted: false, negated });
  }
  return out;
}

function searchWords(text: string): string[] {
  return text
    .normalize('NFKC')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

/**
 * True when `haystack` matches the raw search string: bare words match as
 * prefixes of whole words, a quoted group as an exact phrase, upper-case `OR`
 * joins its two neighbours, and a leading `-` excludes. An empty query matches
 * everything.
 */
export function matchesSearchQuery(haystack: string, raw: string): boolean {
  const tokens = tokenizeSearch(raw);
  if (tokens.length === 0) return true;

  const words = searchWords(haystack);

  const matchesPhrase = (ws: string[]): boolean => {
    for (let i = 0; i + ws.length <= words.length; i++) {
      if (ws.every((w, j) => words[i + j] === w)) return true;
    }
    return false;
  };
  const matchesPrefix = (w: string): boolean => words.some((word) => word.startsWith(w));

  const conjuncts: boolean[] = [];
  let pendingOr = false;
  let excluded = false;
  let budget = MAX_SEARCH_LEXEMES;

  for (const token of tokens) {
    if (budget <= 0) break;
    const isBareWord = !token.quoted && !token.negated;
    if (isBareWord && token.text === 'OR') {
      if (conjuncts.length > 0) pendingOr = true;
      continue;
    }
    if (isBareWord && token.text === 'AND') continue;

    const ws = searchWords(token.text).slice(0, budget);
    if (ws.length === 0) continue;
    budget -= ws.length;

    if (token.negated) {
      if (matchesPhrase(ws)) excluded = true;
      pendingOr = false;
      continue;
    }

    const hit = token.quoted ? matchesPhrase(ws) : ws.every(matchesPrefix);

    if (pendingOr && conjuncts.length > 0) {
      conjuncts.push(conjuncts.pop()! || hit);
      pendingOr = false;
    } else {
      conjuncts.push(hit);
    }
  }

  if (excluded) return false;
  if (conjuncts.length === 0) return true;
  return conjuncts.every(Boolean);
}
